import { Link, useFetcher, useLoaderData } from 'react-router';
import { useState } from 'react';
import { kbEntryType, kbSeverity, kbStackLayer, kbSearchRequest, type KbSearchResponse } from '@repo-manager/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  Input,
  KbSeverityBadge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusBadge,
} from '@repo-manager/ui';

import { getApi } from '../lib/api';
import { getSupabase } from '../lib/supabase';

interface Listing {
  id: string;
  slug: string;
  title: string;
  type: string;
  severity: 'standard' | 'recommended' | 'optional';
  summary: string;
  status: 'draft' | 'active' | 'deprecated';
}

export async function kbIndexLoader(): Promise<{
  entries: Listing[];
  draftCount: number;
}> {
  const supabase = getSupabase();
  const [entriesQ, draftsQ] = await Promise.all([
    supabase
      .from('kb_entries')
      .select('id, slug, title, type, severity, summary, status')
      .order('updated_at', { ascending: false })
      .limit(50),
    supabase
      .from('kb_entries')
      .select('id', { count: 'exact', head: true })
      .eq('status', 'draft'),
  ]);
  return {
    entries: (entriesQ.data ?? []) as Listing[],
    draftCount: draftsQ.count ?? 0,
  };
}

export async function kbSearchAction({ request }: { request: Request }): Promise<KbSearchResponse> {
  const fd = await request.formData();
  const parsed = kbSearchRequest.parse({
    query: String(fd.get('query') ?? ''),
    filters: {
      type: (fd.get('type') as string | null) || undefined,
      stack_layer: (fd.get('stack_layer') as string | null) || undefined,
      severity: (fd.get('severity') as string | null) || undefined,
    },
  });
  return getApi().kbSearch(parsed);
}

const TYPES = kbEntryType.options;
const SEVERITIES = kbSeverity.options;
const LAYERS = kbStackLayer.options;

export function KnowledgePage() {
  const { entries, draftCount } = useLoaderData() as {
    entries: Listing[];
    draftCount: number;
  };
  const fetcher = useFetcher();
  const [filters, setFilters] = useState({
    type: '',
    severity: '',
    stack_layer: '',
  });

  const result = fetcher.data as KbSearchResponse | undefined;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Knowledge base</h1>
          <p className="text-sm text-muted-foreground">Semantic search across patterns, standards, references, and lessons.</p>
        </div>
        <div className="flex items-center gap-2">
          {draftCount > 0 && (
            <Button asChild variant="outline">
              <Link to="/knowledge/drafts">📥 Drafts ({draftCount})</Link>
            </Button>
          )}
          <Button asChild>
            <Link to="/knowledge/new">+ New entry</Link>
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Search</CardTitle>
          <CardDescription>
            <code>slug:&lt;slug&gt;</code> jumps straight to an entry; anything else runs vector search.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <fetcher.Form method="post" className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1 space-y-1">
              <Input name="query" placeholder="how do we validate request bodies" autoComplete="off" />
            </div>
            <Select value={filters.type} onValueChange={(v) => setFilters((f) => ({ ...f, type: v === 'all' ? '' : v }))}>
              <SelectTrigger className="w-40"><SelectValue placeholder="any type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">any type</SelectItem>
                {TYPES.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.severity} onValueChange={(v) => setFilters((f) => ({ ...f, severity: v === 'all' ? '' : v }))}>
              <SelectTrigger className="w-40"><SelectValue placeholder="any severity" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">any severity</SelectItem>
                {SEVERITIES.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.stack_layer} onValueChange={(v) => setFilters((f) => ({ ...f, stack_layer: v === 'all' ? '' : v }))}>
              <SelectTrigger className="w-40"><SelectValue placeholder="any layer" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">any layer</SelectItem>
                {LAYERS.map((l) => (
                  <SelectItem key={l} value={l}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input type="hidden" name="type" value={filters.type} />
            <input type="hidden" name="severity" value={filters.severity} />
            <input type="hidden" name="stack_layer" value={filters.stack_layer} />
            <Button type="submit" disabled={fetcher.state !== 'idle'}>
              {fetcher.state === 'idle' ? 'Search' : 'Searching…'}
            </Button>
          </fetcher.Form>
        </CardContent>
      </Card>

      {result && result.mode === 'search' ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{result.results.length} result{result.results.length === 1 ? '' : 's'}</CardTitle>
          </CardHeader>
          <CardContent>
            {result.results.length === 0 ? (
              <EmptyState title="No matches" description="Loosen filters or try different wording." />
            ) : (
              <ul className="space-y-3">
                {result.results.map((r) => (
                  <li key={r.id} className="rounded-md border border-border p-3">
                    <div className="flex items-center justify-between">
                      <Link to={`/knowledge/${r.slug}`} className="font-medium hover:underline">
                        {r.title}
                      </Link>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{r.type}</Badge>
                        <KbSeverityBadge severity={r.severity} />
                        <span className="text-xs text-muted-foreground">sim {r.similarity.toFixed(3)}</span>
                      </div>
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">{r.summary}</p>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent entries</CardTitle>
        </CardHeader>
        <CardContent>
          {entries.length === 0 ? (
            <EmptyState title="No KB entries yet" description="Seed migration 0010 should have inserted 8." />
          ) : (
            <ul className="divide-y divide-border">
              {entries.map((e) => (
                <li key={e.id} className="flex items-center justify-between gap-3 py-3 text-sm">
                  <div className="min-w-0 flex-1">
                    <Link to={`/knowledge/${e.slug}`} className="font-medium hover:underline">
                      {e.title}
                    </Link>
                    <p className="truncate text-xs text-muted-foreground">{e.summary}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{e.type}</Badge>
                    <KbSeverityBadge severity={e.severity} />
                    {e.status !== 'active' ? <StatusBadge status={e.status} /> : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
